import { useMemo, useCallback, useEffect } from "react";
import domtoimage from "dom-to-image";
import jsPDF from "jspdf";

//Fetch the Date Range Slider Dates for selected granularity
const useRangeSliderDates = (
  eventName,
  dispatch,
  selectedGranularity,
  componentSettings,
  KPIList,
  minMaxDates,
  selectedKPI,
  sliderSize,
  getDateForOneKPI,
  excludeKPIsforSlider
) => {
  useEffect(() => {
    if (selectedGranularity && !minMaxDates) {
      dispatch({
        loading: true,
      });
      dispatch(
        {
          componentSettings,
          selectedGranularity: selectedGranularity,
          selectedKPI: selectedKPI,
          KPIList,
          sliderSize: sliderSize,
          getDateForOneKPI: getDateForOneKPI,
          excludeKPIsforSlider: excludeKPIsforSlider,
        },
        "AxiomEvent",
        eventName
      );
    }
  }, [
    eventName,
    dispatch,
    selectedGranularity,
    componentSettings,
    KPIList,
    minMaxDates,
    selectedKPI,
    sliderSize,
    getDateForOneKPI,
    excludeKPIsforSlider,
  ]);
};

//Get Header Data when referenceDate changes or parent filter changes
const useGetHeaderData = (
  eventName,
  dispatch,
  componentSettings,
  KPIList,
  KPIWiseFilters,
  selectedGranularity,
  sliderStartDate,
  sliderEndDate,
  isParentFilterChange,
  headerConfig,
  localFilterDimensionValues,
  localFilterDimensions,
  isFiltersAvailable,
  resetDrillDown
) => {
  useEffect(() => {
    if (sliderStartDate != null && isParentFilterChange && !resetDrillDown) {
      //Add selectedVal to headers from localfilters
      let newHeaderConfig = [...headerConfig];
      const newLocalFilterDimensions = [...localFilterDimensions];
      const newLocalFilterDimensionValues = [...localFilterDimensionValues];
      dispatch({
        loading: true,
        isParentFilterChange: false,
      });
      newHeaderConfig.forEach((head) => {
        //If the functionality is filter then check if local filter exist
        if (head.functionality === "filter") {
          let indexOfLocalFilter = newLocalFilterDimensions.findIndex((d) => d === head.key);
          //If filter exist then use its selectedVal
          if (indexOfLocalFilter != -1) {
            head.selectedVal = newLocalFilterDimensionValues[indexOfLocalFilter];
          } else {
            head.selectedVal = null;
          }
        }
      });

      dispatch(
        {
          componentSettings,
          KPIWiseFilters,
          KPIList,
          selectedGranularity: selectedGranularity,
          sliderStartDate: sliderStartDate,
          sliderEndDate: sliderEndDate,
          headerConfig: newHeaderConfig,
        },
        "AxiomEvent",
        eventName
      );
    }
  }, [
    eventName,
    dispatch,
    componentSettings,
    KPIList,
    KPIWiseFilters,
    selectedGranularity,
    sliderStartDate,
    sliderEndDate,
    isParentFilterChange,
    headerConfig,
    localFilterDimensionValues,
    localFilterDimensions,
    isFiltersAvailable,
    resetDrillDown,
  ]);
};

//Set the default localFilters after fetching headerData
const useSetDefaultLocalFilters = (
  dispatch,
  sliderStartDate,
  headerData,
  selectedDimension,
  headerConfig,
  isFiltersAvailable
) => {
  useEffect(() => {
    if (isFiltersAvailable && selectedDimension == null && sliderStartDate && headerData) {
      const dimensionDropdown = headerConfig.filter((d) => d.functionality === "filter");
      let newlocalFilterDimensions = [],
        newlocalFilterDimensionValues = [],
        selectedDimension = null;

      dimensionDropdown.forEach((d) => {
        if (d.selectedVal) {
          newlocalFilterDimensions.push(d.key);

          let pushValue;
          if (d.isMultiSelectWithCheckbox) {
            pushValue = d.selectedVal.map((t) =>
              typeof t === "object" && t !== null ? t.value : t
            );
          } else if (Array.isArray(d.selectedVal)) {
            pushValue = d.selectedVal;
          } else {
            pushValue = [d.selectedVal];
          }
          newlocalFilterDimensionValues.push(pushValue);
          selectedDimension = d.key;
        }
      });

      dispatch({
        selectedDimension,
        localFilterDimensionValues: newlocalFilterDimensionValues,
        localFilterDimensions: newlocalFilterDimensions,
      });
    }
  }, [dispatch, sliderStartDate, headerData, selectedDimension, headerConfig, isFiltersAvailable]);
};

const useSetDefaultLocalFiltersForEpic = (
  dispatch,
  sliderStartDate,
  headerData,
  selectedDimension,
  headerConfig,
  isFiltersAvailable
) => {
  useEffect(() => {
    if (isFiltersAvailable && selectedDimension == null && sliderStartDate && headerData) {
      const dimensionDropdown = headerConfig.filter((d) => d.functionality === "filter");
      let newlocalFilterDimensions = [],
        newlocalFilterDimensionValues = [],
        selectedDimension = null;

      dimensionDropdown.forEach((d) => {
        if (d.selectedVal) {
          newlocalFilterDimensions.push(d.key);
          let pushValue = d.isMultiSelectWithCheckbox
            ? d.selectedVal.map((t) => t.value || t)
            : Array.isArray(d.selectedVal)
            ? d.selectedVal
            : [d.selectedVal];
          newlocalFilterDimensionValues.push(pushValue);
          selectedDimension = d.key;
        }
      });

      dispatch({
        selectedDimension,
        localFilterDimensionValues: newlocalFilterDimensionValues,
        localFilterDimensions: newlocalFilterDimensions,
      });
    }
  }, [dispatch, sliderStartDate, headerData, selectedDimension, headerConfig, isFiltersAvailable]);
};

//Reset Drill Down to default value after range slider, dropdown event changed
const useResetDrilldownData = (dispatch, resetDrillDown, headerConfig, drillDownDimensions) => {
  useEffect(() => {
    if (drillDownDimensions && drillDownDimensions.length > 0 && resetDrillDown) {
      const newHeaderConfig = [...headerConfig];
      newHeaderConfig.forEach((header) => {
        if (header.key === "drilldown") {
          header.data.forEach((d, index) => {
            d.displayName = d.defaultDisplayName;
            d.selected = index === 0 ? true : false;
          });
        }
        return header;
      });
      dispatch({
        resetDrillDown: false,
        headerConfig: newHeaderConfig,
        drillDownKey: null,
      });
    }
  }, [dispatch, resetDrillDown, headerConfig, drillDownDimensions]);
};
const useSetGranularityConfig = (dispatch, headerConfig, selectedKPI, KPIWiseConfigs) => {
  useEffect(() => {
    let headerConfigNew;
    dispatch((state) => {
      headerConfigNew = [...state.headerConfig];
      return {};
    });
    if (headerConfigNew && headerConfigNew[1] && KPIWiseConfigs && KPIWiseConfigs[selectedKPI]) {
      headerConfigNew[1].data = KPIWiseConfigs[selectedKPI].granularityConfig.granularities.map(
        (d) => {
          let index = KPIWiseConfigs[selectedKPI].granularityConfig.customGranularities.findIndex(
            (d1) => d == d1.displayKey
          );
          return {
            label:
              KPIWiseConfigs[selectedKPI].granularityConfig.customGranularities[index].displayName,
            value: d,
          };
        }
      );
      headerConfigNew[1].selectedVal =
        KPIWiseConfigs[selectedKPI].granularityConfig.granularities[0];
      dispatch({
        headerConfig: headerConfigNew,
        selectedGranularity: KPIWiseConfigs[selectedKPI].granularityConfig.granularities[0],
      });
    }
  }, [selectedKPI, KPIWiseConfigs, dispatch]);
};

//check that filter dropdown is available or not in config
const useFilterAvailableInDropdown = (componentSettings) => {
  const isFiltersAvailable = useMemo(() => {
    if (componentSettings && componentSettings.headerConfig) {
      let filters = componentSettings.headerConfig.filter((d) => d.functionality === "filter");
      if (filters.length > 0) return true;
      else return false;
    }
  }, [componentSettings]);
  return isFiltersAvailable;
};

//get filter dropdown from config
const usePrepareFilterDropdownFromConfig = (componentSettings) => {
  const filtersDropdown = useMemo(() => {
    if (componentSettings && componentSettings.headerConfig) {
      return componentSettings.headerConfig.filter((d) => d.functionality === "filter");
    }
  }, [componentSettings]);
  return filtersDropdown;
};

const useKPIWiseLocalFilters = (
  localFilterDimensions = [],
  localFilterDimensionValues = [],
  sliderStartDate,
  sliderEndDate,
  KPIList,
  KPIWiseConfigs,
  setWidgetLocalFilters
) => {
  const KPIWiseLocalFilters = useMemo(() => {
    if (KPIWiseConfigs && KPIList) {
      const widgetKPIWiseFilters = {};
      for (const kpi in KPIList) {
        const mainKPIId = KPIList[kpi].mainkpiid;
        const currentKPIConfig = KPIWiseConfigs[mainKPIId];
        // in case of derived KPIs we are adding all the mainKPIs to the widgetKPIList
        if (!currentKPIConfig.displayConfig.ChildKPIIds) {
          widgetKPIWiseFilters[mainKPIId] = {};
          widgetKPIWiseFilters[mainKPIId]["filterDimensions"] = [];
          widgetKPIWiseFilters[mainKPIId]["filterDimensionValues"] = [];

          localFilterDimensions.forEach((dimension, index) => {
            currentKPIConfig["dimensionConfig"]["dimensions"].forEach((dim) => {
              if (dimension == dim.key) {
                widgetKPIWiseFilters[mainKPIId]["filterDimensions"].push(dimension);
                widgetKPIWiseFilters[mainKPIId]["filterDimensionValues"].push(
                  localFilterDimensionValues[index]
                );
              } else if (dimension == dim.matchingKey) {
                widgetKPIWiseFilters[mainKPIId]["filterDimensions"].push(dim.key);
                widgetKPIWiseFilters[mainKPIId]["filterDimensionValues"].push(
                  localFilterDimensionValues[index]
                );
              }
            });
          });
        } else {
          const childSubscribedKPIs = Object.keys(currentKPIConfig.displayConfig.ChildKPIIds);
          const childMainKPIs = Object.keys(KPIWiseConfigs).filter((f) =>
            childSubscribedKPIs.includes(KPIWiseConfigs[f]["displayConfig"]["id"])
          );
          childMainKPIs.forEach((k) => {
            const currentKPIConfig = KPIWiseConfigs[k];
            widgetKPIWiseFilters[k] = {};
            widgetKPIWiseFilters[k]["filterDimensions"] = [];
            widgetKPIWiseFilters[k]["filterDimensionValues"] = [];

            localFilterDimensions.forEach((dimension, index) => {
              currentKPIConfig["dimensionConfig"]["dimensions"].forEach((dim) => {
                if (dimension == dim.key) {
                  widgetKPIWiseFilters[k]["filterDimensions"].push(dimension);
                  widgetKPIWiseFilters[k]["filterDimensionValues"].push(
                    localFilterDimensionValues[index]
                  );
                } else if (dimension == dim.matchingKey) {
                  widgetKPIWiseFilters[mainKPIId]["filterDimensions"].push(dim.key);
                  widgetKPIWiseFilters[mainKPIId]["filterDimensionValues"].push(
                    localFilterDimensionValues[index]
                  );
                }
              });
            });
          });
        }
      }

      widgetKPIWiseFilters["granularityStartDate"] = sliderStartDate;
      widgetKPIWiseFilters["granularityEndDate"] = sliderEndDate;

      if (
        !(
          localFilterDimensions.length &&
          localFilterDimensionValues.length &&
          KPIList &&
          KPIWiseConfigs &&
          sliderStartDate &&
          sliderEndDate
        )
      ) {
        return null;
      }
      return widgetKPIWiseFilters;
    }
    return null;
  }, [
    localFilterDimensions,
    localFilterDimensionValues,
    sliderStartDate,
    sliderEndDate,
    KPIList,
    KPIWiseConfigs,
  ]);

  useEffect(() => {
    setWidgetLocalFilters(KPIWiseLocalFilters);
  }, [KPIWiseLocalFilters, setWidgetLocalFilters]);

  return KPIWiseLocalFilters;
};

//get filter dropdown from config
const usePrepareLocalWidgetFilters = (
  localFilterDimensions,
  localFilterDimensionValues,
  sliderStartDate,
  sliderEndDate,
  dispatch,
  eventName,
  KPIList,
  KPIWiseLocalFilters,
  setKPIWiseLocalFilters
) => {
  const WidgetLocalFilters = useMemo(() => {
    const localFilters = [];
    if (localFilterDimensions && localFilterDimensions.length > 0) {
      localFilterDimensions.forEach((dimension, index) => {
        if (
          localFilterDimensionValues[index] &&
          localFilterDimensionValues[index].indexOf("ALL") == -1
        ) {
          localFilters.push({
            filterDimension: dimension,
            value: localFilterDimensionValues[index],
          });
        }
      });
    }
    return localFilters;
  }, [localFilterDimensionValues, localFilterDimensions]);
  useEffect(() => {
    if (WidgetLocalFilters && KPIWiseLocalFilters == null) {
      dispatch(
        {
          WidgetLocalFilters,
          KPIList,
        },
        "AxiomEvent",
        eventName
      );
    }
  }, [WidgetLocalFilters, dispatch, KPIList, eventName, KPIWiseLocalFilters]);
  useEffect(() => {
    let KPIWiseLocalFiltersTemp = KPIWiseLocalFilters;
    if (!KPIWiseLocalFiltersTemp) {
      KPIWiseLocalFiltersTemp = {};
    }
    KPIWiseLocalFiltersTemp["granularityStartDate"] = sliderStartDate;
    KPIWiseLocalFiltersTemp["granularityEndDate"] = sliderEndDate;
    setKPIWiseLocalFilters(KPIWiseLocalFiltersTemp);
  }, [sliderEndDate, sliderStartDate, KPIWiseLocalFilters, setKPIWiseLocalFilters]);
  return WidgetLocalFilters;
};

//get dimension dropdown from config
const usePrepareDimensionDropdownFromConfig = (componentSettings) => {
  const dimensionDropdownValue = useMemo(() => {
    if (componentSettings && componentSettings.headerConfig) {
      const dimObj = componentSettings.headerConfig.find(
        (d) => d.key === "dimension" && d.type === "selection"
      );
      if (dimObj) {
        return dimObj.selectedVal;
      }
    }
  }, [componentSettings]);
  return dimensionDropdownValue;
};

//get drill down values
const useGetDrillDownFromConfig = (headerConfig) => {
  const drillDownDimensions = useMemo(() => {
    if (headerConfig && headerConfig.length > 0) {
      const drillDownConfig = headerConfig.filter((h) => h.key === "drilldown");
      if (drillDownConfig && drillDownConfig.length > 0) return drillDownConfig[0].data;
    }
  }, [headerConfig]);
  return drillDownDimensions;
};

//Call when Range slider change
const useOnDragEnd = (
  dispatch,
  sliderStartDate,
  drillDownDimensions,
  minMaxDates,
  defaultData,
  componentSettings
) => {
  const onDragEnd = useCallback(
    (data) => {
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);

      if (d3.timeFormat("%Y-%m-%d %H:%M:%S")(startDate) === sliderStartDate) return;

      let refDate = d3.timeFormat("%Y-%m-%d %H:%M:%S")(endDate);
      if (new Date(minMaxDates.endDate).getTime() < new Date(refDate).getTime()) {
        refDate = minMaxDates.endDate;
      }
      const selectedDimensionKey =
        componentSettings &&
        componentSettings.drilldownDimensions &&
        componentSettings.drilldownDimensions[0] &&
        componentSettings.drilldownDimensions[0].key;

      dispatch({
        sliderStartDate: d3.timeFormat("%Y-%m-%d %H:%M:%S")(startDate),
        sliderEndDate: refDate,
        selectedDimension: null,
        isParentFilterChange: true,
        selectedFilters: [],
        selectedDimensionKey: selectedDimensionKey,
        resetDrillDown: drillDownDimensions && drillDownDimensions.length > 0 ? true : false,
        ...defaultData,
      });
    },
    [dispatch, sliderStartDate, drillDownDimensions, minMaxDates, defaultData, componentSettings]
  );
  return onDragEnd;
};

//Call when click on table row to go for next drilldown value
const useOnGoToDrillDown = (
  dispatch,
  drillDownDimensions,
  oldHeaderConfig,
  oldHeaderData,
  defaultData
) => {
  const goToDrillDown = useCallback(
    (data) => {
      if (drillDownDimensions && drillDownDimensions.length > 0) {
        const drillDownObjIndex = drillDownDimensions.findIndex((t) => t.selected);
        if (drillDownObjIndex === drillDownDimensions.length - 1) return;

        const headerConfig = [...oldHeaderConfig];
        const headerData = { ...oldHeaderData };

        headerConfig.map((header) => {
          if (header.key === "drilldown") {
            header.data.forEach((d, index) => {
              if (index === drillDownObjIndex + 1) {
                d.selected = true;
              } else {
                d.selected = false;
              }

              if (index === drillDownObjIndex) {
                d.displayName =
                  data[drillDownDimensions[drillDownObjIndex].displayName] ||
                  data[drillDownDimensions[drillDownObjIndex].dimension];
              }
            });
            headerData[header.key] = header.data;
          }
        });
        dispatch({
          headerConfig: headerConfig,
          headerData,
          drillDownKey: drillDownDimensions[drillDownObjIndex + 1].dimension,
          filterColumn: null,
          searchQuery: "",
          ...defaultData,
        });
      }
    },
    [dispatch, drillDownDimensions, oldHeaderConfig, oldHeaderData, defaultData]
  );
  return goToDrillDown;
};

//Call when click on drilldown on header to go back drilldown value
const useOnChangeView = (
  dispatch,
  drillDownDimensions,
  oldHeaderConfig,
  oldHeaderData,
  defaultData
) => {
  const changeView = useCallback(
    (i, dimension, indexSelected) => {
      const drillDownObjIndex = drillDownDimensions.findIndex((t) => t.selected);

      if (indexSelected >= drillDownObjIndex) return;

      const headerConfig = [...oldHeaderConfig];
      const headerData = { ...oldHeaderData };

      const newHeaderConfig = headerConfig.map((header) => {
        if (header.key === "drilldown") {
          header.data.forEach((d, index) => {
            if (index === indexSelected) {
              d.selected = true;
              d.displayName = d.defaultDisplayName;
            } else {
              d.selected = false;
              d.displayName = index > indexSelected ? d.defaultDisplayName : d.displayName;
            }
          });
          headerData[header.key] = header.data;
        }
        return header;
      });
      dispatch({
        headerConfig: newHeaderConfig,
        headerData,
        drillDownKey: indexSelected === 0 ? null : drillDownDimensions[indexSelected].dimension,
        filterColumn: null,
        searchQuery: "",
        ...defaultData,
      });
    },
    [dispatch, drillDownDimensions, oldHeaderConfig, oldHeaderData, defaultData]
  );
  return changeView;
};

//Call when need to handle change of dimension,kpi or granularity selection
const useOnDropdownChange = (
  dispatch,
  selectedGranularity,
  selectedDimension,
  filtersDropdown,
  componentSettings,
  drillDownDimensions,
  defaultData,
  emptyLocalFilterDimensions = true,
  localFilterDimensions = [],
  localFilterDimensionValues = []
) => {
  const dropdownHandler = useCallback(
    (item, valueOutput) => {
      let value = item.isMultiSelectWithCheckbox
        ? valueOutput.map((itemvalue) => itemvalue.value)
        : valueOutput;
      //If granularity is changed the again fetch minMaxDates
      //If KPI, Dimension or Rolling Peroid change then
      if (
        item.key === "granularity" ||
        item.key === "kpi" ||
        item.key === "dimension" ||
        item.key === "rollingperiod"
      ) {
        const changeObj = {
          resetDrillDown: drillDownDimensions && drillDownDimensions.length > 0 ? true : false,
        };

        if (item.key === "granularity") {
          if (value === selectedGranularity) return;

          changeObj["selectedGranularity"] = value;
          changeObj["sliderStartDate"] = null;
          changeObj["minMaxDates"] = null;
          changeObj["selectedDimension"] = emptyLocalFilterDimensions ? null : selectedDimension;
          changeObj["localFilterDimensionValues"] = emptyLocalFilterDimensions
            ? []
            : localFilterDimensionValues;
          changeObj["localFilterDimensions"] = emptyLocalFilterDimensions
            ? []
            : localFilterDimensions;
          changeObj["sliderSize"] =
            componentSettings.sliderSizes && componentSettings.sliderSizes[value]
              ? componentSettings.sliderSizes[value]
              : componentSettings.sliderSettings.sliderSize;
        } else if (item.key === "kpi") {
          changeObj["selectedDimension"] = null;
          changeObj["localFilterDimensionValues"] = [];
          changeObj["localFilterDimensions"] = [];
          changeObj["selectedKPI"] = value;
          changeObj["minMaxDates"] = null;
        } else if (item.key === "rollingperiod") {
          changeObj["selectedDimension"] = null;
          changeObj["localFilterDimensionValues"] = [];
          changeObj["localFilterDimensions"] = [];
          changeObj["rollingPeriod"] = value;
          changeObj["filterColumn"] = null;
          changeObj["searchQuery"] = "";
        } else {
          changeObj["selectedNewDimension"] = value;
          changeObj["filterColumn"] = null;
          changeObj["searchQuery"] = "";
          changeObj["resetDrillDown"] =
            !componentSettings.drilldownDimMapping &&
            drillDownDimensions &&
            drillDownDimensions.length > 0
              ? true
              : false;
          changeObj["isDrilldownChange"] = componentSettings.drilldownDimMapping ? true : false;
        }
        const selectedDimensionKey =
          componentSettings.drilldownDimensions &&
          componentSettings.drilldownDimensions[0] &&
          componentSettings.drilldownDimensions[0].key;
        dispatch({
          ...changeObj,
          isParentFilterChange: true,
          ...defaultData,
          selectedDimensionKey: selectedDimensionKey,
          selectedFilters: [],
          baseToDate: null,
          baseFromDate: null,
          comparativeToDate: null,
          comparativeFromDate: null,
        });
        dispatch((state) => {
          let headers;
          headers = { ...state.headers };
          headers[item.key] = value;
          if (componentSettings.drilldownDimMapping && headers["drilldown"])
            delete headers["drilldown"];
          return { headers };
        });
      }

      if (item.functionality === "filter") {
        //Find the index of the filter changed
        if (value !== selectedDimension || (value == "ALL" && selectedDimension == "ALL")) {
          dispatch((state) => {
            let indexOfFilter = state.localFilterDimensions.findIndex((i) => i == item.key);

            let localFilterDimensionValues = [...state.localFilterDimensionValues];
            let localFilterDimensions = [...state.localFilterDimensions];
            localFilterDimensionValues[indexOfFilter] = item.isMultiSelectWithCheckbox
              ? value
              : [value];

            const selectedDimensionKey =
              componentSettings.drilldownDimensions &&
              componentSettings.drilldownDimensions[0] &&
              componentSettings.drilldownDimensions[0].key;

            if (indexOfFilter === filtersDropdown.length - 1) {
              let headers;
              headers = { ...state.headers };
              headers[item.key] = valueOutput;
              return {
                selectedFilters: [],
                selectedDimensionKey: selectedDimensionKey,
                selectedDimension: valueOutput,
                localFilterDimensionValues,
                headers,
                resetDrillDown:
                  drillDownDimensions && drillDownDimensions.length > 0 ? true : false,
                ...defaultData,
              };
            } else {
              //Empty filters after the index as the parent filter is changed.
              let newlocalFilterDimensions = [],
                newlocalFilterDimensionValues = [];
              localFilterDimensions.forEach((d, i) => {
                if (
                  i <= indexOfFilter ||
                  (i > indexOfFilter && componentSettings.noFilter == true)
                ) {
                  newlocalFilterDimensions.push(localFilterDimensions[i]);
                  newlocalFilterDimensionValues.push(localFilterDimensionValues[i]);
                }
              });

              let indexOfFilterDropDown = filtersDropdown.findIndex((i) => i.key == item.key);
              let headers = { ...state.headers };
              filtersDropdown.forEach((h, index) => {
                if (index < indexOfFilterDropDown && headers[h.key]) {
                  headers[h.key] = headers[h.key];
                } else if (
                  index > indexOfFilterDropDown &&
                  headers[h.key] &&
                  !componentSettings.noFilter
                ) {
                  delete headers[h.key];
                } else if (
                  index > indexOfFilterDropDown &&
                  headers[h.key] &&
                  componentSettings.noFilter
                ) {
                  headers[h.key] = headers[h.key];
                } else if (index == indexOfFilterDropDown) {
                  headers[h.key] = valueOutput;
                }
              });

              return {
                localFilterDimensions: newlocalFilterDimensions,
                localFilterDimensionValues: newlocalFilterDimensionValues,
                isParentFilterChange: true,
                headerData: null,
                headers: headers,
                selectedDimension: null,
                selectedFilters: [],
                selectedDimensionKey: selectedDimensionKey,
                resetDrillDown:
                  drillDownDimensions && drillDownDimensions.length > 0 ? true : false,
                ...defaultData,
              };
            }
          });
        }
      }
    },
    [
      dispatch,
      selectedGranularity,
      selectedDimension,
      filtersDropdown,
      componentSettings,
      drillDownDimensions,
      defaultData,
      emptyLocalFilterDimensions,
      localFilterDimensions,
      localFilterDimensionValues,
    ]
  );
  return dropdownHandler;
};

//get max values for pivot kpis
const usePrepareMaxValueForPivotData = (pivotData) => {
  const maxValuesForKPIs = useMemo(() => {
    if (pivotData && pivotData.length > 0) {
      const columns = pivotData[0][0].valueKey;
      const columnMaxValueMapping = {};
      columns.forEach((column) => {
        let maxValue = 0;
        pivotData[1].forEach((row, index) => {
          if (row[column.key] && maxValue < row[column.key]) maxValue = row[column.key];
        });
        columnMaxValueMapping[column.key] = maxValue;
      });
      return columnMaxValueMapping;
    }
  }, [pivotData]);
  return maxValuesForKPIs;
};

//Call when search select for pivot
const useOnSearch = (dispatch) => {
  const onSearch = useCallback(
    (column) => {
      dispatch({
        filterColumn: column,
      });
    },
    [dispatch]
  );
  return onSearch;
};

//Call when search query change for pivot
const useOnChangeQuery = (dispatch) => {
  const onChangeQuery = useCallback(
    (value) => {
      dispatch({
        searchQuery: value,
      });
    },
    [dispatch]
  );
  return onChangeQuery;
};

//Set the default drilldown values if it's change based on Dimension
const useSetDefaultDrillDownValue = (
  dispatch,
  headerData,
  headerConfig,
  componentSettings,
  isDrilldownChange,
  selectedNewDimension,
  isKPIChange
) => {
  useEffect(() => {
    if (componentSettings.drilldownDimMapping && isDrilldownChange) {
      const newHeaderConfig = [...headerConfig];
      let newHeaderData = {};

      const dimensionDropdown = newHeaderConfig.find((d) =>
        isKPIChange ? d.key === "kpi" : d.key === "dimension"
      );
      const drillObj = newHeaderConfig.find((d) => d.key === "drilldown");

      if (selectedNewDimension) dimensionDropdown["selectedVal"] = selectedNewDimension;

      const dimMapValue = componentSettings.drilldownDimMapping[selectedNewDimension]
        ? selectedNewDimension
        : componentSettings.drilldownDimMapping[dimensionDropdown.selectedVal]
        ? dimensionDropdown.selectedVal
        : "default";

      if (componentSettings.drilldownDimMapping[dimMapValue]) {
        const dataObj = $.extend(true, {}, componentSettings.drilldownDimMapping[dimMapValue]);
        drillObj.data = [...dataObj.data];
        drillObj.label = dataObj.label;
        if (drillObj.data && drillObj.data.length > 0) {
          drillObj.data[0]["selected"] = true;
          drillObj["defaultDrilldown"] = drillObj.data[0].displayName;
        }
      } else {
        drillObj.data = [];
        drillObj.label = "";
        drillObj["defaultDrilldown"] = dimensionDropdown.label;
      }

      if (headerData) {
        newHeaderData["headerData"] = { ...headerData };
        newHeaderData["headerData"][drillObj.key] = drillObj.data;
      }

      dispatch({
        headerConfig: newHeaderConfig,
        isDrilldownChange: false,
        ...newHeaderData,
        drillDownKey: null,
      });
    }
  }, [
    dispatch,
    headerData,
    headerConfig,
    componentSettings,
    isDrilldownChange,
    selectedNewDimension,
    isKPIChange,
  ]);
};

//Capture Widget Screen shot and make pdf
const useDownloadPDF = (widgetRef, isExpanded, isTwoColumnView) => {
  const downloadPDF = useCallback(
    (name) => {
      if (widgetRef.current != null) {
        const elementToDownload = widgetRef.current;
        const scale = Math.floor(
          window.innerWidth / elementToDownload.getBoundingClientRect().width
        );

        setTimeout(function () {
          domtoimage
            .toPng(elementToDownload, {
              height: elementToDownload.getBoundingClientRect().height * scale,
              width: elementToDownload.getBoundingClientRect().width * scale,
              style: {
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              },
            })
            .then(function (dataUrl) {
              var img = new Image();
              img.src = dataUrl;

              const doc = new jsPDF("l", "px", "a1");
              doc.addImage(
                img,
                "PNG",
                20,
                20,
                elementToDownload.getBoundingClientRect().width *
                  (isExpanded || !isTwoColumnView ? 1 : 2),
                elementToDownload.getBoundingClientRect().height *
                  (isExpanded || !isTwoColumnView ? 1 : 2)
              );
              const fileName = `${name}_${d3.timeFormat("%Y_%m_%d_%H_%M_%S")(new Date())}.pdf`;
              doc.save(fileName);
            });
        }, 10);
      }
    },
    [widgetRef, isExpanded, isTwoColumnView]
  );
  return downloadPDF;
};

const useDownloadPDFWithMultiplePage = (pageRef) => {
  const downloadPDF = useCallback(
    (name) => {
      if (pageRef.current != null) {
        const elementToDownload = pageRef.current;
        const scale = Math.floor(
          window.innerWidth / elementToDownload.getBoundingClientRect().width
        );

        setTimeout(function () {
          domtoimage
            .toPng(elementToDownload, {
              height: elementToDownload.getBoundingClientRect().height * scale,
              width: elementToDownload.getBoundingClientRect().width * scale,
              style: {
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              },
            })
            .then(function (dataUrl) {
              var img = new Image();
              img.src = dataUrl;

              const doc = new jsPDF("p", "px", "a1");
              const pageHeight = doc.internal.pageSize.getHeight();
              const pageWidth = doc.internal.pageSize.getWidth();
              const imgheight = elementToDownload.getBoundingClientRect().height;
              const pagecount = Math.floor(imgheight / pageHeight);
              doc.addImage(img, "PNG", 2, 0, pageWidth - 4, 0);
              if (pagecount > 0) {
                var j = 1;
                while (j != pagecount) {
                  doc.addPage("p", "px", "a1");
                  doc.addImage(img, "PNG", 2, -(j * pageHeight), pageWidth - 4, 0);
                  j++;
                }
              }
              const fileName = `${name}_${d3.timeFormat("%Y_%m_%d_%H_%M_%S")(new Date())}.pdf`;
              doc.save(fileName);
            });
        }, 10);
      }
    },
    [pageRef]
  );
  return downloadPDF;
};

//get range slider label with DB end date
const useFormatArrowLabelWithEndDate = (typeOfTicks, arrowLabelDisplayFormat, dbEndDate) => {
  const formatArrowLabel = useCallback(
    (data) => {
      if (dbEndDate) {
        let startDate = new Date(data.startPoint);
        let endDate = new Date(data.endPoint);
        let labelValue = "";
        switch (typeOfTicks) {
          case "month": {
            const displayFormatForLabel = arrowLabelDisplayFormat ? arrowLabelDisplayFormat : "%b";
            const startMonth = d3.timeFormat(displayFormatForLabel)(startDate);
            const endMonth = d3.timeFormat(displayFormatForLabel)(endDate);
            labelValue =
              startMonth == endMonth
                ? startMonth
                : `${startMonth}-${d3.timeFormat("%b,%Y")(endDate)}`;
            const startYear = d3.timeFormat("%Y")(startDate);
            const endYear = d3.timeFormat("%Y")(endDate);
            labelValue =
              startYear == endYear
                ? labelValue
                : `${d3.timeFormat("%b,%Y")(startDate)}-${d3.timeFormat("%b,%Y")(endDate)}`;
            break;
          }
          case "day": {
            const displayFormatForLabel = arrowLabelDisplayFormat ? arrowLabelDisplayFormat : "%d";
            const startDateValue = d3.timeFormat(displayFormatForLabel)(startDate);
            const endDateValue = d3.timeFormat(displayFormatForLabel)(endDate);
            labelValue =
              startDateValue == endDateValue
                ? startDateValue
                : `${startDateValue}-${d3.timeFormat("%d,%b")(endDate)}`;
            break;
          }
          case "week-monday":
          case "week-sunday": {
            startDate = new Date(d3.timeFormat("%Y-%m-%d 00:00:00")(startDate));
            endDate = new Date(
              d3.timeFormat("%Y-%m-%d 23:59:59")(new Date(endDate.setDate(endDate.getDate() + 6)))
            );

            if (new Date(dbEndDate).getTime() < new Date(endDate).getTime()) {
              const startMonth = d3.timeFormat("%b")(startDate);
              const endMonth = d3.timeFormat("%b")(new Date(dbEndDate));
              labelValue =
                startMonth == endMonth
                  ? `${d3.timeFormat("%d")(startDate)}-${d3.timeFormat("%d,%b")(
                      new Date(dbEndDate)
                    )}`
                  : `${d3.timeFormat("%d,%b")(startDate)}-${d3.timeFormat("%d,%b")(
                      new Date(dbEndDate)
                    )}`;
            } else {
              const startMonth = d3.timeFormat("%b")(startDate);
              const endMonth = d3.timeFormat("%b")(endDate);
              labelValue =
                startMonth == endMonth
                  ? `${d3.timeFormat("%d")(startDate)}-${d3.timeFormat("%d,%b")(endDate)}`
                  : `${d3.timeFormat("%d,%b")(startDate)}-${d3.timeFormat("%d,%b")(endDate)}`;
            }

            break;
          }
          case "yearly-january": {
            const startYear = d3.timeFormat("%Y")(startDate);
            const endYear = d3.timeFormat("%Y")(endDate);
            labelValue = startYear == endYear ? startYear : `${startYear}-${endYear}`;
            break;
          }
          case "yearly-april": {
            startDate = new Date(d3.timeFormat("%Y-%m-%d 00:00:00")(startDate));
            endDate = new Date(endDate.setFullYear(endDate.getFullYear() + 1));
            endDate = new Date(d3.timeFormat("%Y-03-31 23:59:59")(endDate));

            if (new Date(dbEndDate).getTime() < new Date(endDate).getTime()) {
              labelValue = `${d3.timeFormat("%d-%b,%y")(startDate)} - ${d3.timeFormat("%d-%b,%y")(
                new Date(dbEndDate)
              )}`;
            } else {
              labelValue = `${d3.timeFormat("%d-%b,%y")(startDate)} - ${d3.timeFormat("%d-%b,%y")(
                endDate
              )}`;
            }
            break;
          }
          case "quarterly-january":
          case "quarterly-april": {
            startDate = new Date(d3.timeFormat("%Y-%m-%d 00:00:00")(startDate));
            endDate = new Date(
              d3.timeFormat("%Y-%m-%d 23:59:59")(
                new Date(endDate.getFullYear(), endDate.getMonth() + 3, 0)
              )
            );

            if (new Date(dbEndDate).getTime() < new Date(endDate).getTime()) {
              labelValue = `${d3.timeFormat("%b,%y")(startDate)} - ${d3.timeFormat("%b,%y")(
                new Date(dbEndDate)
              )}`;
            } else {
              labelValue = `${d3.timeFormat("%b,%y")(startDate)} - ${d3.timeFormat("%b,%y")(
                endDate
              )}`;
            }
            break;
          }
        }
        return labelValue;
      }
    },
    [typeOfTicks, arrowLabelDisplayFormat, dbEndDate]
  );
  return formatArrowLabel;
};

//get Default rolling Period from config
const usePrepareRollingPeriodDropdownFromConfig = (componentSettings) => {
  const rollingPeriodDropdownValue = useMemo(() => {
    if (componentSettings && componentSettings.headerConfig) {
      const dimObj = componentSettings.headerConfig.find(
        (d) => d.key === "rollingperiod" && d.type === "selection"
      );
      if (dimObj) {
        return dimObj.selectedVal;
      } else {
        return componentSettings.defaultRollingPeriod || 1;
      }
    } else {
      return componentSettings && componentSettings.defaultRollingPeriod
        ? componentSettings.defaultRollingPeriod
        : 1;
    }
  }, [componentSettings]);
  return rollingPeriodDropdownValue;
};

export {
  useRangeSliderDates,
  useGetHeaderData,
  useSetDefaultLocalFilters,
  useResetDrilldownData,
  useFilterAvailableInDropdown,
  usePrepareFilterDropdownFromConfig,
  useGetDrillDownFromConfig,
  useOnDragEnd,
  useOnGoToDrillDown,
  useOnChangeView,
  useOnDropdownChange,
  usePrepareMaxValueForPivotData,
  usePrepareDimensionDropdownFromConfig,
  useOnSearch,
  useOnChangeQuery,
  useSetDefaultDrillDownValue,
  useDownloadPDF,
  useFormatArrowLabelWithEndDate,
  useSetGranularityConfig,
  usePrepareLocalWidgetFilters,
  usePrepareRollingPeriodDropdownFromConfig,
  useKPIWiseLocalFilters,
  useDownloadPDFWithMultiplePage,
  useSetDefaultLocalFiltersForEpic,
};
